import { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { usePartNumberSort } from './usePartNumberSort';
import SubTabToolbar from './SubTabToolbar';
import { useSubTabControls } from './useSubTabControls';

interface StockStatusItem {
  part_number: string;
  description: string;
  stock_quantity: number;
  warehouse_name: string;
  [key: string]: unknown;
}

interface StockStatusSubTabProps {
  partNumber?: string;
}

export default function StockStatusSubTab({ partNumber }: StockStatusSubTabProps) {
  const [items, setItems] = useState<StockStatusItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const { pageSize, setPageSize, searchInput, setSearchInput, search, page, setPage } = useSubTabControls(partNumber);
  const { ordering, icon: sortIcon, toggle: togglePartNumberSort } = usePartNumberSort(() => setPage(1));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = { page, page_size: pageSize };
      if (search) params['search'] = search;
      if (ordering) params['ordering'] = ordering;
      const res = await callOffAPI.getStockStatus(params);
      const data = res.data as { results?: StockStatusItem[]; items?: StockStatusItem[]; count?: number; total_count?: number };
      setItems(data.results || data.items || []);
      setTotalCount(data.count || data.total_count || 0);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load stock status';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, ordering]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div>
      <SubTabToolbar
        search={searchInput}
        onSearchChange={setSearchInput}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
      />
      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No stock status data available.</p>
      ) : (
      <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th
                className="text-left p-2 font-medium cursor-pointer hover:bg-muted/70 select-none"
                onClick={togglePartNumberSort}
              >
                <div className="flex items-center gap-1">Item No. {sortIcon}</div>
              </th>
              <th className="text-left p-2 font-medium">Item Description</th>
              <th className="text-left p-2 font-medium">Warehouse</th>
              <th className="text-right p-2 font-medium">SUM(OnHand)</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i} className="border-b hover:bg-muted/30">
                <td className="p-2 font-mono text-xs">{item.part_number}</td>
                <td className="p-2">{item.description}</td>
                <td className="p-2">{item.warehouse_name}</td>
                <td className="p-2 text-right">{Math.round(Number(item.stock_quantity))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
